import {
  LogisticsCalculationDetailResponseSchema,
  LogisticsCalculationListResponseSchema,
  LogisticsCalculationViewsResponseSchema,
  LogisticsCargoCandidatesResponseSchema,
  type CreateLogisticsCalculationInput,
  type LogisticsCalculationDetailResponse,
  type LogisticsCalculationListResponse,
  type LogisticsCalculationViewsResponse,
  type LogisticsCargoCandidatesResponse,
  type PatchLogisticsCalculationInput,
} from '@magimp/shared';
import { z, type ZodType } from 'zod';
import { request } from './client.js';

const json = (method: string, body?: unknown): RequestInit => ({
  method,
  ...(body === undefined
    ? {}
    : { body: JSON.stringify(body), headers: { 'content-type': 'application/json' } }),
});

export function listLogisticsCalculations(
  signal?: AbortSignal,
): Promise<LogisticsCalculationListResponse> {
  return request<LogisticsCalculationListResponse>(
    '/logistics-calculations',
    json('GET'),
    signal ? { signal } : {},
    LogisticsCalculationListResponseSchema,
  );
}

export function getLogisticsCalculation(
  id: string,
  signal?: AbortSignal,
): Promise<LogisticsCalculationDetailResponse> {
  return request<LogisticsCalculationDetailResponse>(
    `/logistics-calculations/${encodeURIComponent(id)}`,
    json('GET'),
    signal ? { signal } : {},
    LogisticsCalculationDetailResponseSchema as unknown as ZodType<LogisticsCalculationDetailResponse>,
  );
}

export function createLogisticsCalculation(
  input: CreateLogisticsCalculationInput,
): Promise<LogisticsCalculationDetailResponse> {
  return request<LogisticsCalculationDetailResponse>(
    '/logistics-calculations',
    json('POST', input),
    {},
    LogisticsCalculationDetailResponseSchema as unknown as ZodType<LogisticsCalculationDetailResponse>,
  );
}

export function patchLogisticsCalculation(
  id: string,
  input: PatchLogisticsCalculationInput,
): Promise<LogisticsCalculationDetailResponse> {
  return request<LogisticsCalculationDetailResponse>(
    `/logistics-calculations/${encodeURIComponent(id)}`,
    json('PATCH', input),
    {},
    LogisticsCalculationDetailResponseSchema as unknown as ZodType<LogisticsCalculationDetailResponse>,
  );
}

export function publishLogisticsCalculation(
  id: string,
): Promise<LogisticsCalculationDetailResponse> {
  return request<LogisticsCalculationDetailResponse>(
    `/logistics-calculations/${encodeURIComponent(id)}/publish`,
    json('POST'),
    {},
    LogisticsCalculationDetailResponseSchema as unknown as ZodType<LogisticsCalculationDetailResponse>,
  );
}

export function revokeLogisticsCalculation(
  id: string,
): Promise<LogisticsCalculationDetailResponse> {
  return request<LogisticsCalculationDetailResponse>(
    `/logistics-calculations/${encodeURIComponent(id)}/revoke`,
    json('POST'),
    {},
    LogisticsCalculationDetailResponseSchema as unknown as ZodType<LogisticsCalculationDetailResponse>,
  );
}

export function getLogisticsCalculationViews(
  id: string,
  signal?: AbortSignal,
): Promise<LogisticsCalculationViewsResponse> {
  return request<LogisticsCalculationViewsResponse>(
    `/logistics-calculations/${encodeURIComponent(id)}/views`,
    json('GET'),
    signal ? { signal } : {},
    LogisticsCalculationViewsResponseSchema,
  );
}

export function searchLogisticsCargoCandidates(
  query: string,
  signal?: AbortSignal,
): Promise<LogisticsCargoCandidatesResponse> {
  return request<LogisticsCargoCandidatesResponse>(
    '/logistics-calculations/cargo-candidates',
    json('GET'),
    { query: { q: query, limit: 20 }, ...(signal ? { signal } : {}) },
    LogisticsCargoCandidatesResponseSchema,
  );
}

/**
 * One candidate by identity, answering whether it is visible and who holds it.
 *
 * WHY not the search above: it matches an article fragment, needs two
 * characters and answers one page of results, so a one-character article or a
 * common prefix would resolve to the wrong cargo or to none.
 */
export function getLogisticsCargoCandidate(
  id: string,
  signal?: AbortSignal,
): Promise<LogisticsCargoCandidatesResponse> {
  return request<LogisticsCargoCandidatesResponse>(
    '/logistics-calculations/cargo-candidates',
    json('GET'),
    { query: { id }, ...(signal ? { signal } : {}) },
    LogisticsCargoCandidatesResponseSchema,
  );
}

const NoContentSchema = z.void();

export function deleteLogisticsCalculation(id: string): Promise<void> {
  return request<void>(
    `/logistics-calculations/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    {},
    NoContentSchema,
  );
}
